import { useState, useEffect, useCallback } from "react";
import axios from "axios";

import UsersTable from "./UsersTable";
import UserCard from "./UserCard";

import Modal from "../../components/common/Modal";
import ConfirmDialog from "../../components/common/ConfirmDialog";
import StatusScreen from "../../components/common/StatusScreen";

import AddAdminForm from "../../components/users/AddAdminForm";
import TableLoader from "../../components/loaders/TableLoader";

export default function AdminsPage() {
  const [admins, setAdmins] = useState([]);
  const [loadingPage, setLoadingPage] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [lastUpdated, setLastUpdated] = useState(null);

  const [showModal, setShowModal] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [showStatus, setShowStatus] = useState(false);

  const [pendingData, setPendingData] = useState(null);
  const [statusType, setStatusType] = useState("loading");
  const [statusMessage, setStatusMessage] = useState("");

  /** ----------------------------------------------------
   *  FETCH ADMINS — wrapped in useCallback for auto-refresh stability
   *  ---------------------------------------------------- */
  const fetchAdmins = useCallback(async () => {
    console.log("🔄 Refreshing admins...");

    try {
      const response = await axios.get(
        process.env.REACT_APP_BACKEND_BASE_URL + "/api/v1/fetch/users/all",
        { params: { userType: "ADMIN" }, timeout: 5000 }
      );

      const raw = response.data?.data || [];

      const formatted = raw.map((u) => ({
        name: `${u.firstName} ${u.lastName}`,
        email: u.email,
        phone: u.phoneNumber,
        location: u.location,
        identityId: u.identityId,
        role: u.roles,
        status: u.status === "ACTIVE" ? "Active" : "Inactive",
        tractors: u.tractors || null,
        raw: u,
      }));

      setAdmins(formatted);
      setLoadError("");
      setLastUpdated(new Date().toLocaleTimeString());

      if (loadingPage) setLoadingPage(false);
    } catch (err) {
      console.error("❌ Failed to fetch admins:", err);
      setLoadError("Failed to load administrators. Please try again.");
      if (loadingPage) setLoadingPage(false);
    }
  }, [loadingPage]);

  /** ----------------------------------------------------
   *  INITIAL LOAD + AUTO-REFRESH EVERY 5s
   *  ---------------------------------------------------- */
  useEffect(() => {
    fetchAdmins();

    const interval = setInterval(() => {
      fetchAdmins();
    }, process.env.REACT_APP_REFRESH_AFTER);

    return () => clearInterval(interval);
  }, [fetchAdmins]);

  /** ----------------------------------------------------
   *  FORM SUBMIT → show confirmation dialog
   *  ---------------------------------------------------- */
  const handleFormSubmit = (data) => {
    setPendingData(data);
    setShowModal(false);
    setShowConfirm(true);
  };

  /** ----------------------------------------------------
   *  CONFIRM SAVE → Register admin → Reload list
   *  ---------------------------------------------------- */
  const confirmSave = async () => {
    setShowConfirm(false);

    setStatusType("loading");
    setStatusMessage("Saving admin…");
    setShowStatus(true);

    try {
      await axios.post(
        process.env.REACT_APP_BACKEND_BASE_URL + "/api/v1/auth/users/register",
        {
          firstName: pendingData.firstName,
          lastName: pendingData.lastName,
          email: pendingData.email,
          phoneNumber: pendingData.phone,
          userType: "ADMIN",
          role:
            pendingData.role === "Super Admin"
              ? "SUPER_ADMIN"
              : "OPERATIONS_ADMIN",
        }
      );

      await fetchAdmins();

      setStatusType("success");
      setStatusMessage("Admin added successfully!");

      setTimeout(() => setShowStatus(false), 1500);
    } catch (err) {
      console.error("Failed to save admin:", err);

      setStatusType("error");
      setStatusMessage(
        err.response?.data?.message || "Failed to save administrator."
      );

      setTimeout(() => setShowStatus(false), 2000);
    }
  };

  return (
    <div className="dashboard-content min-h-full text-gray-900 dark:text-gray-100">
      {/* Page Loading */}
      {loadingPage && <TableLoader />}

      {/* Load Error */}
      {!loadingPage && loadError && (
        <div className="py-6 text-center text-red-600 dark:text-red-400">
          {loadError}
        </div>
      )}

      {/* CONTENT */}
      {!loadingPage && !loadError && (
        <>
          <div className="dashboard-grid dashboard-grid-1 mb-6">
            <UserCard
              title="Total Admins"
              count={admins.length}
              color="#02007B"
              icon="🧑‍💼"
            />
          </div>

          <UsersTable
            data={admins}
            title="Admin"
            onAddUser={() => setShowModal(true)}
            onViewUser={(user) => alert(JSON.stringify(user, null, 2))}
          />
        </>
      )}

      {/* MODALS */}
      <Modal
        open={showModal}
        title="Add Administrator"
        icon="🧑‍💼"
        onClose={() => setShowModal(false)}
      >
        <AddAdminForm
          onSubmit={handleFormSubmit}
          onClose={() => setShowModal(false)}
        />
      </Modal>

      <ConfirmDialog
        open={showConfirm}
        message="Are you sure you want to save this admin?"
        onConfirm={confirmSave}
        onCancel={() => setShowConfirm(false)}
      />

      <StatusScreen
        open={showStatus}
        status={statusType}
        message={statusMessage}
      />
    </div>
  );
}